import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import Sidebar from './Sidebar.jsx';
import ProfilePopover from './ProfilePopover.jsx';
import CommandPalette from './CommandPalette.jsx';
import NotificationBell from '../components/ui/NotificationBell.jsx';
import { getRouteTitle } from './navConfig.js';
import { DEFAULT_ROUTE } from './routeAccess.js';
import { getInitials } from '@vanilla/utils/avatar.js';
import { sanitizeUrl } from '../utils/sanitize.js';
import { UsersApiService } from '@vanilla/services/crm/UsersApiService.js';

function routeKeyFromPath(pathname) {
    const parts = String(pathname || '/').split('/').filter(Boolean);
    return parts[0] || DEFAULT_ROUTE;
}

function subFromPath(pathname) {
    const parts = String(pathname || '/').split('/').filter(Boolean);
    return parts[1] || null;
}

function LegacyHashRedirect() {
    const navigate = useNavigate();
    useEffect(() => {
        const hash = window.location.hash || '';
        if (!hash.startsWith('#/')) return;
        const raw = hash.slice(1);
        const [pathPart, queryPart] = raw.split('?');
        const path = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
        const search = queryPart ? `?${queryPart}` : '';
        navigate(`${path}${search}`, { replace: true });
    }, [navigate]);
    return null;
}

function NavigateBridge() {
    const navigate = useNavigate();
    useEffect(() => {
        window.__CRM_NAVIGATE__ = (to) => navigate(to);
        return () => { delete window.__CRM_NAVIGATE__; };
    }, [navigate]);
    return null;
}

export default function RootLayout() {
    const { session } = useAuth();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem('crmNavCollapsed') === 'true');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [online, setOnline] = useState(navigator.onLine);
    const [profileOpen, setProfileOpen] = useState(false);

    const route = routeKeyFromPath(location.pathname);
    const sub = subFromPath(location.pathname);
    const pageTitle = getRouteTitle(route, sub);
    const initials = getInitials(session?.name || session?.email || '?');
    const [avatarBlobUrl, setAvatarBlobUrl] = useState(null);

    useEffect(() => {
        let revoked = false;
        let objectUrl = null;
        const load = async () => {
            if (avatarBlobUrl) { try { URL.revokeObjectURL(avatarBlobUrl); } catch {} setAvatarBlobUrl(null); }
            const apiPath = session?.avatarUrl;
            const hasAvatar = session?.hasAvatar || (!!apiPath && apiPath.includes('/avatar'));
            if (!session?.userId || !hasAvatar || !apiPath) return;
            if (apiPath.startsWith('blob:') || apiPath.startsWith('data:')) {
                setAvatarBlobUrl(apiPath);
                return;
            }
            try {
                const res = await UsersApiService.fetchAvatarBlob(session.userId);
                if (revoked) return;
                if (!res?.blob) return;
                objectUrl = URL.createObjectURL(res.blob);
                setAvatarBlobUrl(objectUrl);
            } catch (e) {
                if (e?.code === 'NOT_MODIFIED') return;
                // keep initials on 404/401
            }
        };
        load();
        return () => { revoked = true; if (objectUrl) try { URL.revokeObjectURL(objectUrl); } catch {} };
    }, [session?.userId, session?.avatarUrl, session?.hasAvatar]);

    useEffect(() => {
        localStorage.setItem('crmNavCollapsed', String(collapsed));
    }, [collapsed]);

    useEffect(() => {
        const onOnline = () => setOnline(true);
        const onOffline = () => setOnline(false);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        window.dispatchEvent(new CustomEvent('navChanged', { detail: { route, sub } }));
    }, [route, sub]);

    return (
        <div id="appRoot" className="crm-app">
            <LegacyHashRedirect />
            <NavigateBridge />
            <div id="connectionBanner" className={`connection-banner ${online ? 'hidden' : ''}`} role="alert" aria-live="polite">
                <span className="connection-banner-icon" aria-hidden="true">&#9679;</span>
                <span className="connection-banner-text">Server offline — retrying...</span>
            </div>

            <header className="crm-mobile-appbar md:hidden grid grid-cols-[40px_1fr_40px] items-center gap-2 px-3 sm:px-4 h-14 sm:min-h-[56px] bg-white border-b border-slate-200 shadow-sm shrink-0 z-40 select-none" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                <button
                    type="button"
                    className="flex items-center justify-center size-9 shrink-0 rounded-lg text-foreground hover:bg-slate-100 active:bg-slate-200 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary"
                    id="crmMobileNavToggle"
                    aria-label="Open navigation menu"
                    onClick={() => setMobileOpen(true)}
                >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M3.5 5.5h13M3.5 10h13M3.5 14.5h13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>
                <Link to="/deals" className="justify-self-center flex items-center justify-center min-w-0 flex-1 px-3" aria-label="Bezohminds CRM home" style={{ minWidth: 0 }}>
                    <img
                        src="/src/assets/bezohminds_logo_transparent.png?v=4"
                        alt="Bezohminds CRM"
                        className="object-contain object-center h-11 w-auto max-w-[276px]"
                        style={{ height: '46px', maxWidth: '276px' }}
                    />
                </Link>
                <span className="sr-only">{pageTitle}</span>
                <button
                    type="button"
                    className="justify-self-end flex items-center justify-center size-9 shrink-0 rounded-full bg-[var(--brand-primary)] text-[var(--text-on-brand)] font-semibold text-xs overflow-hidden hover:opacity-90 active:scale-95 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    id="crmMobileProfileBtn"
                    title="Open profile"
                    aria-label="Open profile"
                    onClick={() => setProfileOpen(true)}
                >
                    {avatarBlobUrl ? (
                        <img src={avatarBlobUrl} alt="" className="size-full object-cover" />
                    ) : sanitizeUrl(session?.avatarUrl) && session?.avatarUrl.startsWith('blob:') ? (
                        <img src={sanitizeUrl(session.avatarUrl)} alt="" className="size-full object-cover" />
                    ) : (
                        <span>{initials}</span>
                    )}
                </button>
            </header>

            <Sidebar
                collapsed={collapsed}
                onToggleCollapse={() => setCollapsed((c) => !c)}
                mobileOpen={mobileOpen}
                onCloseMobile={() => setMobileOpen(false)}
                onProfile={() => setProfileOpen(true)}
                initials={initials}
                userName={session?.name || '—'}
                currentRoute={route}
                avatarUrl={session?.avatarUrl || ''}
            />

            <div className="crm-main">
                <section id={`route-${route}`} className="route-section active">
                    <Outlet />
                </section>
            </div>

            <ProfilePopover open={profileOpen} onClose={() => setProfileOpen(false)} />
            <CommandPalette />
            <NotificationBell />
        </div>
    );
}
